import React, { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * ClientCombobox — autocompletato condiviso sul registro clienti
 * centralizzato.
 *
 * Single source of truth per la selezione del cliente in qualunque modulo
 * operativo (Diario, Pratiche Quotidiane, ecc.). Non clona record clienti,
 * non duplica dati: lavora SEMPRE sull'array `clients` passato dall'alto
 * (popolato dall'unico endpoint `GET /api/clients`), e ritorna l'id reale
 * del cliente selezionato.
 *
 * Inline-creation:
 *   Quando il testo digitato non corrisponde esattamente a nessun cliente
 *   esistente, viene mostrato il bottone verde "+ Crea nuovo cliente"
 *   che invoca il callback `onCreateNew(typedName)`. Il chiamante è
 *   responsabile di aprire il global ClientDialog con il nome pre-fillato
 *   e di auto-selezionare il record dopo il save (vedi pattern
 *   `pendingDiaryForm` in `RubricaClientiTab` / `pendingPraticaForm` in
 *   `PraticheQuotidianeTab`).
 *
 * Props
 *   value        — id del cliente attualmente selezionato (o "")
 *   onChange     — (clientId, clientFullName) => void
 *   clients      — array di clienti dal registro centralizzato; richiesto
 *                  schema minimo: { id, fullName, clientType? }
 *   onCreateNew  — (typedName) => void, opzionale. Se omesso la riga
 *                  "+ Crea nuovo cliente" non viene mostrata.
 *   placeholder  — testo placeholder dell'input
 *   testId       — data-testid del campo input (default: "client-input")
 */
export default function ClientCombobox({
  value,
  onChange,
  clients = [],
  onCreateNew,
  placeholder = "Digita per cercare cliente...",
  testId = "client-input",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.toLowerCase();
    return clients.filter(c => (c.fullName || "").toLowerCase().includes(q));
  }, [clients, search]);

  const selectedName = useMemo(() => {
    const c = clients.find(cl => cl.id === value);
    return c?.fullName || "";
  }, [clients, value]);

  const exactMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return false;
    return clients.some(c => (c.fullName || "").toLowerCase() === q);
  }, [clients, search]);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={open ? search : selectedName}
        onChange={e => { setSearch(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(""); }}
        placeholder={placeholder}
        className="h-9 text-sm"
        data-testid={testId}
        // Browser-autofill suppression now provided by default by the
        // shadcn Input wrapper (lib/no-autofill.js). No explicit
        // autoComplete here — letting the stronger default win
        // (Edge ignores plain "off", honours the random token).
      />
      {open && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-[250px] overflow-y-auto"
          style={{ overscrollBehavior: "contain" }}
        >
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-gray-400 text-center">Nessun cliente trovato</p>
          ) : filtered.map(c => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex justify-between items-center"
              onClick={() => { onChange(c.id, c.fullName); setOpen(false); setSearch(""); }}
              data-testid={`client-option-${c.id}`}
            >
              <span className="font-medium">{c.fullName}</span>
              <span className="text-xs text-gray-400">{c.clientType}</span>
            </button>
          ))}
          {onCreateNew && search.trim() && !exactMatch && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm border-t border-slate-100 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 flex items-center gap-2 font-medium"
              onClick={() => { onCreateNew(search.trim()); setOpen(false); }}
              data-testid="client-combobox-create-new"
            >
              <span className="text-base leading-none">+</span>
              <span>Crea nuovo cliente "{search.trim()}"</span>
            </button>
          )}
        </div>
      )}
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
}

export { ClientCombobox };
